import { Card, Stack, Badge, Button, Group, ActionIcon, Text, TextInput, Popover, Pill, ScrollArea, Table } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconSearch, IconGripVertical } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteResource, useRemove, useReorderModifiers } from "../../../shared/api";
import { PageHeader, EmptyState, MoneyDisplay, ResourceForm, confirmDelete, InfiniteTable, type FormField } from "../../../shared/ui";
import { ProductMultiSelectField } from "../components/ProductMultiSelectField";

/**
 * Sayfalanmış bir kaynağın TÜM sayfalarını arka planda toplar.
 * KÜÇÜK/sınırlı listeler için kullanılır (kategori gibi). Ürün gibi binlerce
 * kayda çıkabilen listelerde KULLANMA — server-side aranan picker kullan
 * (bkz. ProductMultiSelectField), aksi halde tüm liste DOM'a yüklenir.
 */
function useAllOf(resource: string) {
  const r = useInfiniteResource<any>(resource);
  useEffect(() => {
    if (r.hasNextPage && !r.isFetchingNextPage) r.fetchNextPage();
  }, [r.hasNextPage, r.isFetchingNextPage, r.fetchNextPage]);
  return r.items;
}

export function ModifiersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  // Modifier listesi — gerçek sayfalama, scroll'da sonraki 20 backend'den.
  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("modifiers", { search: debouncedSearch });

  // Kategori adları — küçük/sınırlı liste, form options ve tablo için tam
  // liste güvenli. Ürünler ise picker ile seçilir (binlerce olabilir);
  // adlarını modifier'ın kendi product_names alanından alırız.
  const cats = useAllOf("categories");

  const remove = useRemove("modifiers");
  const reorder = useReorderModifiers();
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  // Sürüklenen satırın id'si — sürükleme görsel geri bildirimi için.
  const [dragId, setDragId] = useState<string | null>(null);

  // id → ad eşlemesi (kategoriler); satır başına .find() yerine O(1) lookup.
  const catNameById = useMemo(() => {
    const m = new Map<string, string>();
    cats.forEach((c: any) => m.set(c.id, c.name));
    return m;
  }, [cats]);

  const catOptions = useMemo(
    () => cats.map((c: any) => ({ value: c.id, label: c.name })),
    [cats],
  );

  const fields: FormField[] = useMemo(() => [
    { name: "name", label: "Ad", required: true, placeholder: "Ekstra peynir, Acısız vs." },
    { name: "description", label: "Açıklama", type: "textarea" },
    { name: "price_minor", label: "Ek Fiyat", type: "money", defaultValue: 0, min: 0 },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
    {
      name: "categories",
      label: "Kategoriler",
      type: "multiselect",
      options: catOptions,
      placeholder: "İçecekler, Tatlılar...",
      description: "Bu modifier hangi kategorideki ürünlere uygulansın. Boş bırakılırsa kategori bazlı eşleşme olmaz.",
      defaultValue: [],
    },
    {
      name: "products",
      label: "Ürünler",
      type: "custom",
      defaultValue: [],
      // Düz MultiSelect tüm ürünleri DOM'a basardı — binlerce üründe patlar.
      // Bunun yerine server-side aranan ProductPickerModal kullanan widget.
      render: ({ value, onChange }) => (
        <ProductMultiSelectField
          value={value || []}
          onChange={onChange}
          initialNames={editing?.product_names || {}}
          label="Ürünler"
          description="Sadece seçili ürünlere ek olarak uygulanır. Kategori seçimi yeterli ise boş bırakın."
        />
      ),
    },
  ], [catOptions, editing]);

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Modifier silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  // İlk açılış (arama yok) ve hiç kayıt yok → boş durum.
  const isEmpty = !isLoading && total === 0 && !debouncedSearch;

  // Sürükle-bırak yalnızca arama yokken anlamlı — search filtresi varken
  // gönderilen "order" listesi tüm modifier'ları kapsamaz, sıralama bozulur.
  const dragEnabled = !debouncedSearch && !hasNextPage;

  // Sürükleme sırasında anlık yeniden dizilen yerel satır listesi.
  // list değişince (veri yenilenince) tekrar senkronlanır.
  const [rows, setRows] = useState<any[]>(list);
  useEffect(() => {
    setRows(list);
  }, [list]);

  // Tüm sayfaları yüklemek için dragEnabled olmadığında otomatik fetch.
  // (arama boşsa kullanıcı sıralamak isteyebilir → arka planda eksik
  // sayfaları çek; aktif kullanıcı 100+ modifier için tek seferde
  // hazır olsun.)
  useEffect(() => {
    if (!debouncedSearch && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [debouncedSearch, hasNextPage, isFetchingNextPage, fetchNextPage]);

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from === -1 || to === -1) {
      setDragId(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    setDragId(null);
    reorder.mutate(
      next.map((r) => r.id),
      {
        onSuccess: () =>
          notifications.show({ message: "Sıralama kaydedildi", color: "green" }),
        onError: () => {
          notifications.show({ message: "Sıralama kaydedilemedi", color: "red" });
          setRows(list);
        },
      },
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Modifier'lar"
        subtitle={total > 0 ? `${total} özelleştirme seçeneği` : "Özelleştirme seçenekleri"}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Modifier
          </Button>
        }
      />
      {isEmpty ? (
        <EmptyState title="Modifier yok" description="Soğansız / Ekstra peynir gibi ürün özelleştirmeleri tanımlayın." />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between" wrap="nowrap">
              <TextInput
                placeholder="Modifier ara…"
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                w={260}
              />
              <Text size="xs" c="dimmed">
                {isLoading
                  ? "Yükleniyor…"
                  : `${rows.length} / ${total} gösteriliyor`}
              </Text>
            </Group>

            {debouncedSearch ? (
              // Arama aktif → drag-drop devre dışı, eski sayfalı liste
              <InfiniteTable
                rows={list}
                rowKey={(m) => m.id}
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                emptyContent={
                  <Text size="sm" c="dimmed">"{debouncedSearch}" ile eşleşen modifier yok.</Text>
                }
                columns={modifierColumns({ catNameById, onEdit: (m) => { setEditing(m); open(); }, onDelete: handleDelete })}
              />
            ) : (
              // Arama yok → drag-drop'lu düz tablo
              <ScrollArea>
                <Text size="xs" c="dimmed" mb={6}>
                  {dragEnabled
                    ? "Satırları tutamaçtan (⠿) sürükleyerek sırayı değiştirebilirsiniz."
                    : "Tüm modifier'lar yükleniyor — sıralama bitince aktif olacak…"}
                </Text>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={36} />
                      <Table.Th>Ad</Table.Th>
                      <Table.Th>Kategoriler</Table.Th>
                      <Table.Th>Ürünler</Table.Th>
                      <Table.Th ta="right">Ek Fiyat</Table.Th>
                      <Table.Th>Durum</Table.Th>
                      <Table.Th></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((m: any) => (
                      <Table.Tr
                        key={m.id}
                        draggable={dragEnabled}
                        onDragStart={() => dragEnabled && setDragId(m.id)}
                        onDragOver={(e) => dragEnabled && dragId && e.preventDefault()}
                        onDrop={() => dragEnabled && handleDrop(m.id)}
                        onDragEnd={() => setDragId(null)}
                        {...(dragId === m.id ? { bg: "var(--mantine-color-blue-0)" } : {})}
                        style={{ opacity: dragId === m.id ? 0.5 : 1 }}
                      >
                        <Table.Td>
                          <IconGripVertical
                            size={16}
                            color="var(--mantine-color-gray-5)"
                            style={{ cursor: dragEnabled ? "grab" : "not-allowed" }}
                          />
                        </Table.Td>
                        <Table.Td fw={500}>{m.name}</Table.Td>
                        <Table.Td>
                          <ScopeCell
                            ids={m.categories || []}
                            nameById={catNameById}
                            noun="kategori"
                            title={`"${m.name}" şu kategorilerde`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ScopeCell
                            ids={m.products || []}
                            nameById={m.product_names || {}}
                            noun="ürün"
                            title={`"${m.name}" şu ürünlerde`}
                          />
                        </Table.Td>
                        <Table.Td ta="right" fw={700}>
                          {m.price_minor > 0
                            ? <MoneyDisplay minor={m.price_minor} />
                            : <span style={{ opacity: 0.5 }}>Ücretsiz</span>}
                        </Table.Td>
                        <Table.Td>
                          {m.is_active
                            ? <Badge color="green">Aktif</Badge>
                            : <Badge color="gray">Pasif</Badge>}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <ActionIcon variant="subtle" onClick={() => { setEditing(m); open(); }}>
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(m.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {rows.length === 0 && !isLoading && (
                      <Table.Tr>
                        <Table.Td colSpan={7} ta="center" c="dimmed" py="xl">
                          Henüz modifier yok.
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="modifiers"
        title={editing ? "Modifier Düzenle" : "Yeni Modifier"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}

/**
 * Arama aktifken InfiniteTable için kolon tanımları — drag tutamacı yok.
 */
function modifierColumns(args: {
  catNameById: Map<string, string>;
  onEdit: (m: any) => void;
  onDelete: (id: string) => void;
}) {
  const { catNameById, onEdit, onDelete } = args;
  return [
    { header: "Ad", render: (m: any) => <span style={{ fontWeight: 500 }}>{m.name}</span> },
    {
      header: "Kategoriler",
      render: (m: any) => (
        <ScopeCell
          ids={m.categories || []}
          nameById={catNameById}
          noun="kategori"
          title={`"${m.name}" şu kategorilerde`}
        />
      ),
    },
    {
      header: "Ürünler",
      render: (m: any) => (
        <ScopeCell
          ids={m.products || []}
          nameById={m.product_names || {}}
          noun="ürün"
          title={`"${m.name}" şu ürünlerde`}
        />
      ),
    },
    {
      header: "Ek Fiyat",
      align: "right" as const,
      render: (m: any) => (
        <span style={{ fontWeight: 700 }}>
          {m.price_minor > 0 ? <MoneyDisplay minor={m.price_minor} /> : <span style={{ opacity: 0.5 }}>Ücretsiz</span>}
        </span>
      ),
    },
    {
      header: "Durum",
      render: (m: any) =>
        m.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>,
    },
    {
      header: "",
      render: (m: any) => (
        <Group gap={4}>
          <ActionIcon variant="subtle" onClick={() => onEdit(m)}><IconEdit size={16} /></ActionIcon>
          <ActionIcon variant="subtle" color="red" onClick={() => onDelete(m.id)}><IconTrash size={16} /></ActionIcon>
        </Group>
      ),
    },
  ];
}

/**
 * Kapsam hücresi — virgülle uzayan liste yerine "N kategori" rozeti.
 * Tıklanınca Popover içinde tüm adları chip listesi olarak gösterir,
 * böylece 50 kategoride bile satır yüksekliği sabit kalır.
 */
function ScopeCell({
  ids,
  nameById,
  noun,
  title,
}: {
  ids: string[];
  /** id→ad; Map (kategoriler) veya plain object (modifier.product_names). */
  nameById: Map<string, string> | Record<string, string>;
  noun: string;
  title: string;
}) {
  const names = useMemo(() => {
    const lookup = (id: string) =>
      nameById instanceof Map ? nameById.get(id) : nameById[id];
    return ids.map(lookup).filter(Boolean) as string[];
  }, [ids, nameById]);

  if (names.length === 0) {
    return <Text size="xs" c="dimmed">—</Text>;
  }

  return (
    <Popover width={280} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <Button variant="light" size="compact-xs" radius="xl">
          {names.length} {noun}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="xs" fw={600}>{title}</Text>
          <ScrollArea.Autosize mah={220}>
            <Group gap={4}>
              {names.map((n) => (
                <Pill key={n}>{n}</Pill>
              ))}
            </Group>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
